import Vue from "vue";
import Router from "vue-router";
// import store from '@/core/services/store'; // ✅ This points to index.js in the store folder

import ApiService from '@/core/services/api.service';
import { authGet, migrateAuthStorage } from "@/core/services/auth-storage";
import { canAccess } from "@/core/plugins/Subscription.js";

// Hash-mode app: real paths without # (or with a leftover path + hash) break routing
// and show the login page. Normalize to http://localhost:8080/#/...
(function redirectPathToHash() {
  if (typeof window === "undefined") return;
  migrateAuthStorage();
  const { pathname, search, hash } = window.location;
  if (!pathname || pathname === "/") return;
  if (/\.[a-z0-9]+$/i.test(pathname)) return;

  const nextHash =
    hash && hash.length > 1
      ? hash
      : `#${pathname}${search || ""}`;

  window.location.replace(`${window.location.origin}/${nextHash}`);
})();

// Updated router.beforeEach guard
Vue.use(Router);


const router = new Router({
  mode: "hash",
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/view/pages/auth/login_pages/Login-new"),
      meta: { public: true },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("@/view/pages/auth/login_pages/Login-new"),
      meta: { public: true },
    },

    {
      path: "/admin",
      component: () => import("@/view/layout/Layout"),
      children: [
        {
          path: "/",
          name: "admindashboard",
          component: () => import("@/view/pages/admin/Dashboard.vue"),
        },
        {
          path: "businesses",
          name: "businesses",
          component: () => import("@/view/pages/admin/Businesses.vue"),
        },
        {
          path: "billing",
          name: "billing",
          component: () => import("@/view/pages/admin/Billing.vue"),
        },

        {
          path: "businesses/edit",
          redirect: { name: "businesses" },
        },
        {
          path: "businesses/:id",
          name: "businesses.profile",
          component: () => import("@/view/pages/admin/BusinessProfile.vue"),
        },
        {
          path: "reservation",
          component: () => import("@/view/pages/admin/Reservation.vue"),
        },
        {
          path: "categories",
          component: () => import("@/view/pages/admin/Categories.vue"),
        },
        {
          path: "dish-properties",
          component: () => import("@/view/pages/admin/DishPropeties.vue"),
        },
        {
          path: "dish-properties-group",
          component: () => import("@/view/pages/admin/PropertiesGroup.vue"),
        },
        {
          path: "toppings",
          component: () => import("@/view/pages/admin/Toppings.vue"),
        },
        {
          path: "topping-groups",
          component: () => import("@/view/pages/admin/ToppingGroups.vue"),
        },
        {
          path: "items",
          component: () => import("@/view/pages/admin/Items.vue"),
        },
        {
          path: "opening-times",
          component: () => import("@/view/pages/admin/OpeningTimes.vue"),
        },
        {
          path: "delivery-collection-timings",
          component: () => import("@/view/pages/admin/DeliveryCollectionTimings.vue"),
        },
        {
          path: "orders",
          component: () => import("@/view/pages/admin/Orders.vue"),
        },
        {
          path: "order-items",
          component: () => import("@/view/pages/admin/OrderItems.vue"),
        },
        {
          path: "vouchers",
          component: () => import("@/view/pages/admin/VoucherCodes.vue"),
        },
        {
          path: "user-vouchers",
          component: () => import("@/view/pages/admin/UserVoucherCodes.vue"),
        },
        {
          path: "transaction-fee-setting",
          component: () => import("@/view/pages/admin/CardFeeSetting.vue"),
        },
        {
          path: "agents",
          component: () => import("@/view/pages/admin/Agents.vue"),
        },
        {
          path: "leads",
          component: () => import("@/view/pages/admin/Leads.vue"),
        },
        {
          path: "email-pricing",
          component: () => import("@/view/pages/business/EmailPricing.vue"),
        },
        {
          path: "sms-pricing",
          component: () => import("@/view/pages/business/SmsPricing.vue"),
        },

      ],
    },
    {
      path: "/",
      redirect: "/dashboard",
      component: () => import("@/view/layout/Layout"),
      children: [
        {
          path: "/dashboard",
          name: "dashboard",
          component: () => import("@/view/pages/Dashboard.vue"),
        },
        { 
          path: "/orders",
          name: "orders",
          component: () => import("@/view/pages/error/Error.vue"),
          children: [
            {
              path: "today",
              name: "today",
              component: () => import("@/view/pages/orders/Today.vue"),
              meta: { permission: 'view orders' }

            },
            {
              path: "pending",
              name: "pending",
              component: () => import("@/view/pages/orders/Pending.vue"),
              meta: { permission: 'view orders' }

            },
            {
              path: "detail",
              name: "detail",
              props: true,
              component: () => import("@/view/pages/orders/OrderDetail.vue"),
              meta: { permission: 'view orders' }

            },
            {
              path: "history",
              name: "history",
              component: () => import("@/view/pages/orders/OrderHistory.vue"),
              meta: { permission: 'view orders' }

            },
            {
              path: "cancelled",
              name: "cancelled",
              component: () => import("@/view/pages/orders/OrderHistoryCancelled.vue"),
              meta: { permission: 'view orders' }

            },
             {
              path: "upcoming",
              name: "upcoming",
              component: () => import("@/view/pages/orders/UpcomingOrders.vue"),
              meta: { permission: 'view orders' }

            },

            {
              path: "combined",
              name: "combined",
              component: () => import("@/view/pages/orders/OrderHistoryCombined.vue"),
              meta: { permission: 'view orders' }

            },
          ],
          meta: { permission: 'view orders' }

        },
        {
          path: "/menu-manager",
          name: "menu-manager",
          component: () => import("@/view/pages/error/Error.vue"),
          children: [
            {
              path: "categories",
              name: "categories",
              component: () => import("@/view/pages/menu/Category.vue"),
              meta: { permission: 'view menues' }

            },
            {
              path: "variants",
              name: "variants",
              component: () => import("@/view/pages/menu/Variants.vue"),
              meta: { permission: 'view menues' }

            },
            {
              path: "products",
              name: "products",
              component: () => import("@/view/pages/menu/Product.vue"),
              meta: { permission: 'view menues' }

            },
            {
              path: "toppings",
              name: "toppings",
              component: () => import("@/view/pages/menu/Topping.vue"),
              meta: { permission: 'view menues' }

            },
            {
              path: "properties",
              name: "properties",
              component: () => import("@/view/pages/menu/DishProperty.vue"),
              meta: { permission: 'view menues' }

            },
          ],
        },
        {
          path: "/marketing/campaigns",
          name: "marketing-campaigns",
          component: () => import("@/view/pages/marketing/MarketingCampaign.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/email-templates",
          name: "email-templates",
          component: () => import("@/view/pages/marketing/EmailTemplate.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/campaigns/create",
          name: "create-campaign",
          component: () => import("@/view/pages/marketing/CreateCampaign.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/campaigns/:id/edit",
          name: "edit-campaign",
          component: () => import("@/view/pages/marketing/CreateCampaign.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/customer-lists",
          name: "customer-lists",
          component: () => import("@/view/pages/marketing/CustomerList.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/customer-lists/create",
          name: "create-customer-list",
          component: () => import("@/view/pages/marketing/CreateCustomerList.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/customer-lists/:id",
          name: "view-customer-list",
          component: () => import("@/view/pages/marketing/ViewCustomerList.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/marketing/customer-lists/:id/edit",
          name: "edit-customer-list",
          component: () => import("@/view/pages/marketing/EditCustomerList.vue"),
          meta: { permission: undefined, feature: "emailSmsMarketing" },
        },
        {
          path: "/system-setting",
          name: "system-setting",
          component: () => import("@/view/pages/error/Error.vue"),
          children: [
            {
              path: "contact-details",
              name: "wizard-contact-details",
              component: () => import("@/view/pages/business/components/ContactInfo.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "restaurant-details",
              name: "wizard-restaurant-details",
              component: () => import("@/view/pages/business/components/RestaurantType.vue"),
                 meta: { permission: 'view system settings' }

            },
            {
              path: "menu-details",
              name: "wizard-menu-details",
              component: () => import("@/view/pages/business/components/Menu.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "social-link-details",
              name: "wizard-social-link-details",
              component: () => import("@/view/pages/business/components/Social.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "branding-details",
              name: "wizard-branding-details",
              component: () => import("@/view/pages/business/components/Branding.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "opening-timings-details",
              name: "wizard-opening-timings-details",
              component: () => import("@/view/pages/business/components/OpeningTimes.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "delivery-charges-details",
              name: "wizard-delivery-charges-details",
              component: () => import("@/view/pages/business/components/DeliveryCharge.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "delivery-collection-details",
              name: "wizard-collection-details",
              component: () => import("@/view/pages/business/components/DeliveryCollectionTimeInfo.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "domain",
              name: "domain",
              component: () => import("@/view/pages/business/Domain.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "billing",
              name: "billings",
              component: () => import("@/view/pages/business/Billing.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "qrcode",
              name: "qrcode",
              component: () => import("@/view/pages/business/QrCode.vue"),
              meta: { permission: 'view system settings' }

            },
            {
              path: "account-delete",
              name: "account-delete",
              component: () => import("@/view/pages/business/AccountDelete.vue"),
            },
            {
              path: "loyality-settings",
              name: "loyality-settings",
              component: () => import("@/view/pages/business/loyality-settings.vue"),
            },
          ],
        },
        {
          path: "/business",
          name: "business",
          component: () => import("@/view/pages/error/Error.vue"),
          children: [
            {
              path: "staff",
              name: "staff",
             component: () => import("@/view/pages/business/components/Staff.vue"),
             meta: { permission: 'view staff' }

            }, 
            {
              path: "announcements",
              name: "announcements",
              component: () => import("@/view/pages/business/Announcements.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "offers",
              name: "offers",
              component: () => import("@/view/pages/business/Offers.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "announcement",
              name: "announcement",
              component: () => import("@/view/pages/business/Announcement.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "info",
              name: "info",
              component: () => import("@/view/pages/business/Info.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "branches",
              name: "branches",
              component: () => import("@/view/pages/business/branches/list.vue"),
              children: [
                {
                  path: "add",
                  name: "add-branch",
                  component: () => import("@/view/pages/business/branches/add.vue"),
                },
                {
                  path: ":id",
                  name: "edit-branch",
                  component: () => import("@/view/pages/business/branches/edit.vue"),
                },
              ]
            }, {
              path: "addbranch",
              name: "addbranch",
              component: () => import("@/view/pages/business/branches/add.vue"),
            },
            {
              path: "contact-us",
              name: "messages",
              component: () => import("@/view/pages/business/Messages.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "customers",
              name: "users",
              component: () => import("@/view/pages/business/Users.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "templateinfo",
              name: "templateinfo",
              component: () => import("@/view/pages/business/TemplateInfo.vue"),
              meta: { permission: 'view business settings' }

            },
            
            {
              path: "reservations",
              name: "reservations",
              component: () => import("@/view/pages/business/Reservations.vue"),
              meta: { permission: 'view reservations' }

            },
            {
              path: "room-reservations",
              name: "room-reservations",
              component: () => import("@/view/pages/business/RoomReservations.vue"),
              meta: { permission: 'view reservations' }

            },
            {
              path: "package-bookings",
              name: "package-bookings",
              component: () => import("@/view/pages/business/PackageBookings.vue"),
              meta: { permission: 'view reservations' }
            },
            {
              path: "advanced-sales-reports",
              name: "advanced-sales-reports",
              component: () => import("@/view/pages/business/AdvancedSalesReports.vue"),
              meta: { permission: undefined, feature: "advancedSalesReports" },
            },
            {
              path: "reservation-settings",
              name: "reservation-settings",
              component: () => import("@/view/pages/business/components/settings/ReservationSettings.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "gift-vouchers",
              name: "gift-vouchers",
              component: () => import("@/view/pages/business/GiftVouchers.vue"),
              meta: { permission: 'view business settings' }
            },
            {
              path: "packages",
              name: "packages",
              component: () => import("@/view/pages/business/Packages.vue"),
              meta: { permission: 'view business settings' }
            },

            {
              path: "delivery-collection",
              name: "delivery-collection",
              component: () => import("@/view/pages/business/DeliveryInfo.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "opening-times",
              name: "opening-times",
              component: () => import("@/view/pages/business/OpeningTime.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "wizard",
              redirect: "/business/settings"
            },
            {
              path: "settings",
              name: "settings",
              component: () => import("@/view/pages/business/Setting.vue"),
              meta: { permission: 'view business settings' }

            },
            {
              path: "pos-import",
              redirect: "/menu-manager/products",
            },
          ],
        },
      ],
    },
    {
      path: "/custom-error",
      name: "error",
      component: () => import("@/view/pages/error/Error.vue"),
      children: [
        {
          path: "error-1",
          name: "error-1",
          component: () => import("@/view/pages/error/Error-1.vue"),
        },
        {
          path: "error-2",
          name: "error-2",
          component: () => import("@/view/pages/error/Error-2.vue"),
        },
        {
          path: "error-3",
          name: "error-3",
          component: () => import("@/view/pages/error/Error-3.vue"),
        },
        {
          path: "error-4",
          name: "error-4",
          component: () => import("@/view/pages/error/Error-4.vue"),
        },
        {
          path: "error-5",
          name: "error-5",
          component: () => import("@/view/pages/error/Error-5.vue"),
        },
        {
          path: "error-6",
          name: "error-6",
          component: () => import("@/view/pages/error/Error-6.vue"),
        },
      ],
    },
    {
      path: "/forgot-password",
      component: () => import("@/view/pages/auth/Forgot-password"),
      meta: { public: true },
    },
    {
      path: "/reset-password/:token",
      component: () => import("@/view/pages/auth/Forgot-password-reset"),
      meta: { public: true },
    },
    {
      path: "/business-claim/:token",
      component: () => import("@/view/pages/auth/login_pages/Claim-business"),
      meta: { public: true },
    },

    {
      path: "*",
      redirect: "/404",
    },
    {
      // the 404 route, when none of the above matches
      path: "/404",
      name: "404",
      component: () => import("@/view/pages/error/Error-1.vue"),
    },
  ],
});
function hasPermission(userPermissions, requiredPermission) {
  if (!requiredPermission) return true; // No permission required
  if (!userPermissions) return false; // No permissions provided
  return userPermissions.some(perm => perm.name === requiredPermission);
}

function getHomeRoute() {
  const isAgent = authGet('is_agent') === '1';
  if (isAgent) return '/admin/leads';

  try {
    const userAuth = authGet('USER_STATE');
    const authParsed = userAuth ? JSON.parse(userAuth) : null;
    // Super admin / agent portal: normal === false
    if (authParsed && authParsed.normal === false) {
      return '/admin';
    }
  } catch (e) {
    // fall through to business dashboard
  }

  return '/dashboard';
}

function isPublicAuthRoute(to) {
  return (
    to.name === 'login' ||
    to.name === 'register' ||
    to.path === '/login' ||
    to.path === '/register' ||
    to.path === '/forgot-password' ||
    to.path.startsWith('/reset-password') ||
    to.path.startsWith('/business-claim')
  );
}

function isAdminFrontPath(path) {
  return path === '/admin' || path.startsWith('/admin/');
}

function isAgentAllowedAdminPath(path) {
  return path === '/admin/leads' || path.startsWith('/admin/leads/');
}

function isImpersonatingUser() {
  return authGet('is_impersonating') === 'true';
}

function isSuperAdminUser() {
  if (isImpersonatingUser()) return false;
  if (String(authGet('is_superAdmin')) === '1') return true;
  if (authGet('is_agent') === '1') return false;
  try {
    const authParsed = JSON.parse(authGet('USER_STATE') || 'null');
    return !!(authParsed && authParsed.normal === false);
  } catch (e) {
    return false;
  }
}

// Router guard implementation
router.beforeEach(async (to, from, next) => {
  if (to.name === '404') {
    return next();
  }

  const token = authGet('ID_TOKEN');
  const isAgent = authGet('is_agent') === '1';
  const isStaff = authGet('is_staff') === '1';

  // Logged-in users hitting login/register/home should go to their dashboard
  if (token && isPublicAuthRoute(to)) {
    return next(getHomeRoute());
  }

  // Empty home "/" (from /#/ ) — send authenticated users home, guests to login
  if (to.path === '/' || to.path === '') {
    if (token) {
      return next(getHomeRoute());
    }
    return next({ name: 'login' });
  }

  // Guests may access public auth pages
  if (isPublicAuthRoute(to)) {
    return next();
  }

  if (!token) {
    return next({ name: 'login' });
  }

  const featureRecord = [...to.matched].reverse().find((record) => record.meta && record.meta.feature);
  if (featureRecord && !canAccess(featureRecord.meta.feature)) {
    return next({ path: "/system-setting/billing" });
  }

  if (isAdminFrontPath(to.path)) {
    if (isAgent) {
      if (!isAgentAllowedAdminPath(to.path)) {
        return next('/admin/leads');
      }
    } else if (!isSuperAdminUser()) {
      return next('/dashboard');
    }
  }

  // Super admin (not impersonating): keep them on the admin app, not the restaurant dashboard
  if (isSuperAdminUser() && (to.path === '/dashboard' || to.path === '/')) {
    return next('/admin');
  }

  // Agents: keep them off business dashboard
  if (isAgent && (to.path === '/dashboard' || to.path === '/')) {
    return next('/admin/leads');
  }

  // Check permissions for staff and agent users
  if (isStaff || isAgent) {
    try {
      let userPermissions = [];

      if (isStaff) {
        userPermissions = JSON.parse(authGet('staff_permissions') || '[]');
      } else {
        userPermissions = JSON.parse(authGet('agent_permissions') || '[]');
      }

      const routePermission = to.meta.permission;

      if (!hasPermission(userPermissions, routePermission)) {
        return next('/404');
      }
    } catch (e) {
      console.error('Permission check error:', e);
      return next('/error-1');
    }
  }

  next();
});

export default router;


